import React, { useEffect, useMemo, useState } from "react";
import { Download, Share2, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { drawCard, toBlob, printSet } from "@/lib/teenCard";
import { THEMES, FACTS_CARD, CARD_UI } from "@/lib/shareCards";
import { downloadBlob } from "@/lib/downloads";
import { useLang } from "@/lib/lang";

const FACTS_THEME = { id: "facts", bg: "#0B1730", accent: "#D4AF37" };

const toCard = (theme, data, lang) => ({ kicker: theme.name[lang], title: data[0], body: data[1] });

const CardTile = ({ card, palette, size, name, lang, testid }) => {
  const ui = CARD_UI[lang];
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    let live = true;
    drawCard(card, palette, size, lang).then(toBlob).then((b) => {
      if (!live) return;
      u = URL.createObjectURL(b); setBlob(b); setUrl(u);
    }).catch((e) => console.error("card render failed", e));
    return () => { live = false; if (u) URL.revokeObjectURL(u); };
  }, [card, palette, size, lang]);
  const file = `BSN-${name}-${size}${lang === "es" ? "-es" : ""}.png`;
  const save = () => { downloadBlob(file, blob, "image/png"); toast.success(ui.saved); };
  const send = async () => {
    const f = new File([blob], file, { type: "image/png" });
    if (navigator.canShare?.({ files: [f] })) {
      try { await navigator.share({ files: [f], title: card.title }); } catch { /* closed */ }
    } else save();
  };
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className={`w-full rounded-2xl overflow-hidden card-tactile bg-bsn-midnight ${size === "story" ? "aspect-[9/16]" : "aspect-square"}`}>
        {url ? <img src={url} alt={card.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 text-bsn-gold animate-spin" /></div>}
      </div>
      <div className="mt-3 flex gap-2">
        <button disabled={!blob} onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full btn-gold text-sm font-semibold" data-testid={`${testid}-save`}><Download className="w-4 h-4" /> {ui.save}</button>
        <button disabled={!blob} onClick={send} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-bsn-midnight text-bsn-parchment text-sm font-semibold hover:bg-bsn-navy" data-testid={`${testid}-send`}><Share2 className="w-4 h-4" /> {ui.send}</button>
      </div>
    </div>
  );
};

const Pill = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-bsn-midnight text-bsn-champagne border-bsn-midnight" : "bg-white text-bsn-midnight border-bsn-midnight/20 hover:border-bsn-gold"}`} data-testid={testid}>{children}</button>
);

export const TeenShareCard = () => {
  const { lang } = useLang();
  const ui = CARD_UI[lang];
  const [themeId, setThemeId] = useState("facts");
  const [size, setSize] = useState("square");
  const [busy, setBusy] = useState(false);
  const theme = THEMES.find((t) => t.id === themeId);
  const facts = FACTS_CARD[lang];
  const cards = useMemo(() => (theme ? theme.cards.map((d) => toCard(theme, d[lang], lang)) : [facts]), [theme, facts, lang]);
  const palette = theme || FACTS_THEME;

  const print = async () => {
    setBusy(true);
    try { await printSet(cards, palette, lang, theme ? theme.name[lang] : facts.kicker, `BSN-cards-${themeId}${lang === "es" ? "-es" : ""}.pdf`); toast.success(ui.printing); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-14 rounded-3xl bg-white border-t-4 border-bsn-gold card-tactile p-6 sm:p-10" data-testid="teen-share-section">
      <p className="eyebrow mb-2">{ui.sectionEyebrow}</p>
      <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-midnight">{ui.sectionTitle}</h2>
      <p className="mt-3 text-bsn-sapphire/75 max-w-2xl">{ui.sectionSub}</p>
      <div className="mt-6 flex flex-wrap gap-2" data-testid="card-theme-tabs">
        <Pill on={themeId === "facts"} onClick={() => setThemeId("facts")} testid="card-theme-facts">{ui.featured}</Pill>
        {THEMES.map((t) => <Pill key={t.id} on={themeId === t.id} onClick={() => setThemeId(t.id)} testid={`card-theme-${t.id}`}>{t.name[lang]} · {t.cards.length}</Pill>)}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Pill on={size === "square"} onClick={() => setSize("square")} testid="card-size-square">{ui.square}</Pill>
        <Pill on={size === "story"} onClick={() => setSize("story")} testid="card-size-story">{ui.story}</Pill>
        <button onClick={print} disabled={busy} className="sm:ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold" data-testid="card-print-set">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} {ui.printSet}
        </button>
      </div>
      <div className={`mt-8 grid gap-8 ${size === "story" ? "grid-cols-2 md:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {cards.map((card, i) => (
          <CardTile key={`${themeId}-${i}-${lang}`} card={card} palette={palette} size={size} lang={lang} name={`${themeId}-${i + 1}`} testid={`share-card-${themeId}-${i + 1}`} />
        ))}
      </div>
    </section>
  );
};
